/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { Box, useTheme } from '@mui/material';

/** Glyph artwork plus its exported size, in px of its finish's design tile. */
export interface MetallicGlyphArt {
  src: string;
  width: number;
  height: number;
}

/** One glyph drawn for each finish: the dark set on the 96px tile, the light set on the 64px one. */
export interface MetallicDockGlyph {
  dark: MetallicGlyphArt;
  light: MetallicGlyphArt;
}

// Design px of a tile -> share of the rendered tile, so the artwork scales to any button size.
const tileUnits = (tileSize: number) => (px: number) => `${(px / tileSize) * 100}cqw`;
const darkUnits = tileUnits(96);
const lightUnits = tileUnits(64);

const TILE_FINISH = {
  // Gunmetal tile, brushed-silver glyph.
  dark: {
    units: darkUnits,
    borderRadius: darkUnits(22.5),
    // Glyphs render larger than in the Figma tile (63px box) to fill the small dock buttons.
    glyphScale: 1.2,
    // Badged glyphs stay centred on the tile.
    glyphFrame: undefined,
    backgroundImage: [
      'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 8.33%, rgba(255, 255, 255, 0) 45.83%, rgba(255, 255, 255, 0.05) 91.67%)',
      'linear-gradient(180deg, rgb(76, 81, 90) 0%, rgb(45, 49, 55) 50%, rgb(28, 30, 35) 100%)',
    ].join(', '),
    border: 'none',
    boxShadow: [
      `0 ${darkUnits(6)} ${darkUnits(15)} rgba(0, 0, 0, 0.5)`,
      `inset 0 ${darkUnits(-3)} ${darkUnits(6)} rgba(0, 0, 0, 0.35)`,
      `inset 0 ${darkUnits(1.5)} 0 rgba(255, 255, 255, 0.16)`,
    ].join(', '),
  },
  // Brushed-silver tile with its own graphite glyph set and subtle colour accents (V4 option A-2).
  light: {
    units: lightUnits,
    borderRadius: lightUnits(15),
    glyphScale: 1,
    // Glyphs sit in a centred 60px frame; badges spill past its right and bottom edges.
    glyphFrame: 60,
    backgroundImage: [
      'linear-gradient(135deg, rgba(255, 255, 255, 0.6) 8.33%, rgba(255, 255, 255, 0) 45.83%, rgba(255, 255, 255, 0.35) 91.67%)',
      'linear-gradient(180deg, rgb(255, 255, 255) 0%, rgb(238, 241, 244) 55%, rgb(211, 216, 223) 100%)',
    ].join(', '),
    border: `${lightUnits(1)} solid rgb(255, 255, 255)`,
    boxShadow: [
      `0 ${lightUnits(1)} ${lightUnits(1.5)} rgba(27, 36, 51, 0.1)`,
      `0 ${lightUnits(4)} ${lightUnits(10)} rgba(27, 36, 51, 0.16)`,
      `inset 0 ${lightUnits(-2)} ${lightUnits(4)} rgba(138, 147, 163, 0.22)`,
      `inset 0 ${lightUnits(1)} 0 rgb(255, 255, 255)`,
    ].join(', '),
  },
} as const;

/**
 * Gunmetal app tile with a brushed-silver glyph (V4 metallic direction, option A); silver with a
 * graphite glyph in light mode.
 */
export function MetallicDockIcon({
  glyph,
  inverted = false,
}: Readonly<{
  glyph: MetallicDockGlyph;
  /**
   * Active state: the opposite finish, so the tile stands out from the other apps. Dark mode
   * inverts its gunmetal tile to a light one (accent hues kept); light mode switches to the
   * gunmetal tile and silver glyph, since inverting the silver tile only turns it flat black.
   */
  inverted?: boolean;
}>) {
  const mode = useTheme().palette.mode;
  const showInverted = inverted && mode === 'dark';
  const artMode = inverted && mode === 'light' ? 'dark' : mode;
  const finish = TILE_FINISH[artMode];
  const { units, glyphScale, glyphFrame } = finish;
  const { src, width, height } = glyph[artMode];
  return (
    <Box sx={{ width: '100%', height: '100%', containerType: 'size' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          borderRadius: finish.borderRadius,
          filter: showInverted ? 'invert(1) hue-rotate(180deg)' : 'none',
          transition: 'filter 240ms ease',
          backgroundImage: finish.backgroundImage,
          border: finish.border,
          boxShadow: finish.boxShadow,
        }}
      >
        <Box
          component="img"
          src={src}
          alt=""
          aria-hidden
          sx={{
            flexShrink: 0,
            width: units(width * glyphScale),
            height: units(height * glyphScale),
            // Centre the frame rather than the glyph, so badges overhang as in the design.
            ...(glyphFrame && {
              mr: units(glyphFrame - width),
              mb: units(glyphFrame - height),
            }),
          }}
        />
      </Box>
    </Box>
  );
}
